import {
    getFirm,
    getFirmInvitees,
    getFirmMemberRoles,
    getFirmMembers,
    getFirms,
    getUserFirms,
    updateFirm,
    updateFirmMemberRoles,
} from './http/access-ws/firms';
import { getFirmGroups } from './http/access-ws/groups';
import type { QueryClient } from '@tanstack/query-core';
import type { UseMutationOptions } from '@tanstack/react-query';
import type {
    FirmWithParentsAndChildren,
    FirmMembershipWithProfile,
    UpdateFirm,
} from '@thinkalpha/ui-types/types/admin/firms';
import type { UpdateFirmMemberRoles } from 'src/lib/user/types';

// Constants
const FIRMS = 'firms';
const FIRM_MEMBERSHIP = 'firm-membership';
const GROUPS = 'groups';

export const getFirmQuery = (firmId: string | undefined | null) => ({
    queryKey: [FIRMS, firmId],
    queryFn: () => getFirm(firmId!),
    enabled: Boolean(firmId),
});

export const getFirmsQuery = () => ({
    queryKey: [FIRMS],
    queryFn: () => getFirms(),
});

export const getUserFirmsQuery = (userId: string | undefined) => ({
    queryKey: [FIRMS, userId],
    queryFn: () => getUserFirms(userId!),
    enabled: Boolean(userId),
});

export const getFirmGroupsQuery = (firmId: string) => ({
    queryKey: [FIRMS, GROUPS, firmId],
    queryFn: () => getFirmGroups(firmId!),
    enabled: Boolean(firmId),
});

export const getFirmMembersQuery = (firmId: string | undefined) => ({
    queryKey: [FIRMS, firmId, FIRM_MEMBERSHIP],
    queryFn: () => getFirmMembers(firmId!),
    enabled: Boolean(firmId),
});

export const getFirmMemberRolesQuery = (firmId: string | undefined, firmMemberId: string | undefined) => {
    return {
        queryKey: [FIRMS, firmId, FIRM_MEMBERSHIP, firmMemberId, 'roles'],
        queryFn: () => getFirmMemberRoles(firmId!, firmMemberId!),
        enabled: Boolean(firmId && firmMemberId),
    };
};

export const getFirmInviteesQuery = (firmId: string | undefined) => ({
    queryKey: [FIRMS, firmId, 'firmInvitees'],
    queryFn: () => getFirmInvitees(firmId!),
    enabled: Boolean(firmId),
});

// mutations
export const updateFirmMutation = (
    restMutationOptions?: UseMutationOptions<Awaited<ReturnType<typeof updateFirm>>, Error, UpdateFirm>,
): UseMutationOptions<FirmWithParentsAndChildren, Error, UpdateFirm> => {
    return {
        mutationFn: updateFirm,
        ...restMutationOptions,
    };
};

export const updateFirmMemberRolesMutation = (
    restMutationOptions?: UseMutationOptions<
        Awaited<ReturnType<typeof updateFirmMemberRoles>>,
        Error,
        UpdateFirmMemberRoles
    >,
): UseMutationOptions<FirmMembershipWithProfile, Error, UpdateFirmMemberRoles> => {
    return {
        mutationFn: updateFirmMemberRoles,
        ...restMutationOptions,
    };
};

// cache-invalidation functions

// membership invalidation function is not needed - this will invalidate membership query
export const invalidateFirmQuery = (queryClient: QueryClient, firmId: string) => {
    queryClient.invalidateQueries({ queryKey: [FIRMS, firmId] });
};

export const invalidateFirmsQuery = (queryClient: QueryClient) => {
    queryClient.invalidateQueries({ queryKey: [FIRMS], exact: true });
};

export const invalidateUserFirmsQuery = (queryClient: QueryClient, userId: string) => {
    queryClient.invalidateQueries({ queryKey: [FIRMS, userId] });
};

export const invalidateFirmMembershipQuery = (queryClient: QueryClient, firmId: string) => {
    queryClient.invalidateQueries({ queryKey: [FIRMS, firmId, FIRM_MEMBERSHIP], exact: true });
};

export const invalidateFirmMemberRolesQuery = (queryClient: QueryClient, variables: UpdateFirmMemberRoles) => {
    queryClient.invalidateQueries({
        queryKey: [FIRMS, variables.firmId, FIRM_MEMBERSHIP, variables.firmMemberId, 'roles'],
    });
};

// cache-updating functions

export const updateFirmQueryCache = (
    queryClient: QueryClient,
    firmId: string,
    updatedFirm: FirmWithParentsAndChildren,
) => {
    // The PATCH /firms/{id} response is typed as FirmWithParentsAndChildren but
    // does not always include `children` / `parents`. Merge with the prior
    // cache entry so a partial response doesn't strip relationship data that
    // downstream consumers (e.g., the LAB Billing Rule Member Firm dropdown)
    // rely on.
    queryClient.setQueryData<FirmWithParentsAndChildren>([FIRMS, firmId], (prev) => {
        if (!prev) return updatedFirm;
        return {
            ...prev,
            ...updatedFirm,
            children: updatedFirm.children ?? prev.children,
            parents: updatedFirm.parents ?? prev.parents,
        };
    });
};

export const updateFirmMemberRolesCache = (
    queryClient: QueryClient,
    updatedFirmMemberRoles: Array<string>,
    variables: UpdateFirmMemberRoles,
) => {
    queryClient.setQueryData([FIRMS, variables.firmId, FIRM_MEMBERSHIP, variables.firmMemberId, 'roles'], () => [
        ...updatedFirmMemberRoles,
    ]);
};
